import React, { useEffect, useState } from 'react';
import tournamentService from '../services/tournamentService';
import { toast } from 'sonner';
import { FiDownloadCloud, FiSave, FiLayers } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import '../styles/AdminDashboard.css';

const TournamentRoundMultipliers = () => {
    const { t } = useTranslation();
    const [tournaments, setTournaments] = useState([]);
    const [selectedId, setSelectedId] = useState('');
    const [loadingList, setLoadingList] = useState(true);
    const [rounds, setRounds] = useState([]);
    const [loadingRounds, setLoadingRounds] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [saving, setSaving] = useState(false);

    // Load FOOTBALL tournaments (rounds are pulled from API-Football)
    useEffect(() => {
        const fetchTournaments = async () => {
            setLoadingList(true);
            try {
                const data = await tournamentService.getAllTournaments();
                const football = (Array.isArray(data) ? data : [])
                    .filter(tt => (tt.sportType || '').toUpperCase() === 'FOOTBALL')
                    .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
                setTournaments(football);
            } catch (e) {
                console.error('Failed to load tournaments', e);
                toast.error('Nepodařilo se načíst turnaje');
            } finally {
                setLoadingList(false);
            }
        };
        fetchTournaments();
    }, []);

    // Load existing rounds whenever a tournament is selected
    useEffect(() => {
        if (!selectedId) {
            setRounds([]);
            return;
        }
        const fetchRounds = async () => {
            setLoadingRounds(true);
            try {
                const data = await tournamentService.getTournamentRounds(selectedId);
                setRounds(normalizeRounds(data));
            } catch (e) {
                console.error('Failed to load rounds', e);
                toast.error('Nepodařilo se načíst kola turnaje');
                setRounds([]);
            } finally {
                setLoadingRounds(false);
            }
        };
        fetchRounds();
    }, [selectedId]);

    // Keep the original multiplier alongside the edited value to detect changes
    const normalizeRounds = (data) =>
        (Array.isArray(data) ? data : [])
            .map(r => ({
                id: r.id,
                roundName: r.roundName,
                multiplier: r.multiplier ?? 1,
                original: r.multiplier ?? 1,
            }))
            .sort((a, b) => (a.roundName || '').localeCompare(b.roundName || '', undefined, { numeric: true }));

    const handleSync = async () => {
        if (!selectedId) return;
        setSyncing(true);
        try {
            const data = await tournamentService.syncTournamentRounds(selectedId);
            setRounds(normalizeRounds(data));
            toast.success('Kola byla stažena z API-Football');
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se stáhnout kola: ${detail}`);
        } finally {
            setSyncing(false);
        }
    };

    const handleMultiplierChange = (roundId, value) => {
        setRounds(prev => prev.map(r =>
            r.id === roundId ? { ...r, multiplier: value } : r
        ));
    };

    const handleSave = async () => {
        const changed = rounds.filter(r => Number(r.multiplier) !== Number(r.original));
        if (changed.length === 0) {
            toast.info('Žádné změny k uložení');
            return;
        }
        // Validate before sending
        for (const r of changed) {
            const m = Number(r.multiplier);
            if (!Number.isInteger(m) || m < 1 || m > 10) {
                toast.error(`Neplatný multiplikátor u kola "${r.roundName}" (povoleno celé číslo 1–10)`);
                return;
            }
        }
        setSaving(true);
        try {
            await Promise.all(changed.map(r =>
                tournamentService.updateTournamentRoundMultiplier(selectedId, r.id, Number(r.multiplier))
            ));
            setRounds(prev => prev.map(r => ({ ...r, original: Number(r.multiplier) })));
            toast.success(`Uloženo ${changed.length} změn`);
        } catch (error) {
            const detail = error?.response?.data?.message || error?.response?.data || error?.message || 'Neznámá chyba';
            toast.error(`Nepodařilo se uložit multiplikátory: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const hasChanges = rounds.some(r => Number(r.multiplier) !== Number(r.original));

    return (
        <div className="admin-dashboard-container">
            <h2 className="admin-section-title">Multiplikátory kol</h2>
            <div className="admin-action-card" style={{ maxWidth: 640 }}>
                <div className="admin-action-header">
                    <span className="admin-action-icon"><FiLayers /></span>
                    <h3>Body za fázi turnaje</h3>
                </div>

                <p className="admin-action-desc">
                    Vyber fotbalový turnaj a stáhni jeho kola z API-Football. Ke každému kolu nastav
                    násobič bodů (celé číslo 1–10). Násobič se uplatní při bodování tipů na zápasy daného
                    kola, která skončí po uložení.
                </p>

                <div className="admin-action-body">
                    <div className="admin-field">
                        <label htmlFor="trm-select">Turnaj *</label>
                        <select
                            id="trm-select"
                            className="admin-select"
                            value={selectedId}
                            onChange={(e) => setSelectedId(e.target.value)}
                            disabled={loadingList || syncing || saving}
                        >
                            {loadingList ? (
                                <option value="">Načítám turnaje…</option>
                            ) : tournaments.length === 0 ? (
                                <option value="">Žádné FOOTBALL turnaje</option>
                            ) : (
                                <>
                                    <option value="">— Vyberte turnaj —</option>
                                    {tournaments.map(tournament => (
                                        <option key={tournament.id} value={tournament.id}>
                                            {t(`tournaments.${tournament.name}`, { year: tournament.year })}
                                        </option>
                                    ))}
                                </>
                            )}
                        </select>
                    </div>

                    {selectedId && (
                        <button
                            className="admin-action-btn"
                            onClick={handleSync}
                            disabled={syncing || saving || loadingRounds}
                            style={{ marginBottom: 8 }}
                        >
                            {syncing ? 'Stahuji…' : <><FiDownloadCloud /> Stáhnout kola z API-Football</>}
                        </button>
                    )}

                    {loadingRounds && (
                        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Načítám kola…</p>
                    )}

                    {selectedId && !loadingRounds && rounds.length === 0 && (
                        <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>
                            Pro tento turnaj zatím nejsou žádná kola. Stáhni je z API-Football.
                        </p>
                    )}

                    {rounds.length > 0 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                            {rounds.map(r => (
                                <div
                                    key={r.id}
                                    className="admin-field"
                                    style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}
                                >
                                    <label style={{ flex: 1, marginBottom: 0 }}>{r.roundName}</label>
                                    <input
                                        className="admin-input"
                                        type="number"
                                        min="1"
                                        max="10"
                                        step="1"
                                        value={r.multiplier}
                                        onChange={(e) => handleMultiplierChange(r.id, e.target.value)}
                                        disabled={saving}
                                        style={{ width: 90 }}
                                    />
                                    <span style={{ color: '#6b7280', fontSize: '0.9rem' }}>×</span>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {rounds.length > 0 && (
                    <button
                        className="admin-action-btn"
                        onClick={handleSave}
                        disabled={saving || !hasChanges}
                    >
                        {saving ? 'Ukládám…' : <><FiSave /> Uložit multiplikátory</>}
                    </button>
                )}
            </div>
        </div>
    );
};

export default TournamentRoundMultipliers;
